// Sound (spec FE-33, FE-24): horn sounds, synthesised from the design system's note patterns, the
// game's other sounds (sfx.ts), and the park behind a round (ambience.ts, the one audio file).
// Nothing plays before the player's first tap on the page (Lagos Life review item 4) and nothing
// plays while sound is off. Sound starts on at every visit; the volume is remembered on the phone.
import { W } from '../ui/wahala';
import { isPaused, onPause } from './pause';

const VOLUME_KEY = 'wahala.volume';
export type Volume = 'low' | 'normal' | 'loud';
const GAIN: Record<Volume, number> = { low: 0.45, normal: 1, loud: 1.7 };
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let tapped = false;
const listeners = new Set<(on: boolean) => void>();

// Sound is on at the start of every visit. Off lasts until the page closes, so a player who
// silenced it once never comes back to a mute game wondering why.
let on = true;
try {
  localStorage.removeItem('wahala.sound'); // an off saved by an older build
} catch {
  // storage blocked in the frame: nothing was saved
}

export function soundOn(): boolean {
  return on;
}

export function setSound(next: boolean): void {
  on = next;
  for (const l of listeners) l(on);
}
const isOn = soundOn;

let volumeMemory: Volume | null = null;
export function volume(): Volume {
  if (volumeMemory) return volumeMemory;
  try {
    const v = localStorage.getItem(VOLUME_KEY);
    return v === 'low' || v === 'loud' ? v : 'normal';
  } catch {
    return 'normal';
  }
}
export function setVolume(v: Volume): void {
  volumeMemory = v;
  try {
    localStorage.setItem(VOLUME_KEY, v);
  } catch {
    // storage blocked: the choice lasts until the page closes
  }
  if (master) master.gain.value = GAIN[v];
  for (const l of listeners) l(isOn());
}

/**
 * The audio graph's way out, or null when nothing may play: before the first tap, with sound off, or
 * while Lagos Life has the game paused. Everything goes through one master gain (the volume).
 */
export function output(): { ctx: AudioContext; out: AudioNode } | null {
  if (!tapped || !ctx || !isOn() || isPaused()) return null;
  if (!master) {
    master = ctx.createGain();
    master.gain.value = GAIN[volume()];
    master.connect(ctx.destination);
  }
  return { ctx, out: master };
}

export function onSound(cb: (on: boolean) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Call once at boot: the first tap anywhere unlocks audio for the rest of the visit. */
export function unlockOnFirstTap(): void {
  const unlock = () => {
    tapped = true;
    try {
      ctx ??= new AudioContext();
      void ctx.resume();
    } catch {
      ctx = null; // no Web Audio here: horns stay silent
    }
  };
  addEventListener('pointerdown', unlock, { once: true, capture: true });
  addEventListener('keydown', unlock, { once: true, capture: true });
}

/** Play a horn once (the plain horn when the id is unknown). */
// paused by Lagos Life (a sheet, the background): every sound stops until it resumes
onPause((p) => {
  if (!ctx) return;
  if (p) void ctx.suspend().catch(() => {});
  else void ctx.resume().catch(() => {});
});

export function playHorn(id: string | undefined): void {
  const o = output();
  if (!o) return;
  const { ctx } = o;
  const horns = W().horns;
  const horn = horns.find((h) => h.id === id) ?? horns[0];
  if (!horn) return;
  const out = ctx.createGain();
  out.gain.value = 0.18;
  // soften the square and saw waves so a horn honks rather than buzzes
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = 2600;
  tone.connect(out).connect(o.out);
  let t = ctx.currentTime + 0.01;
  for (const [freqs, ms, wave] of horn.steps) {
    const len = ms / 1000;
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = wave ?? 'square';
      osc.frequency.value = f;
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(1 / freqs.length, t + 0.012);
      env.gain.setValueAtTime(1 / freqs.length, t + Math.max(0.012, len - 0.03));
      env.gain.linearRampToValueAtTime(0, t + len);
      osc.connect(env).connect(tone);
      osc.start(t);
      osc.stop(t + len + 0.02);
    }
    t += len;
  }
}
